import React from 'react';

import { Navigate, useLocation } from 'react-router';

import { appRoutes } from '@clearverify/constants';

import { FullscreenLoader } from 'src/components/common';
import { useMaintenanceStatus } from 'src/hooks/app';
import { useCurrentUserAuthStatus } from 'src/hooks/auth';
import { useStripeCustomerPaymentMethods } from 'src/hooks/stripe';
import { useCurrentUser } from 'src/hooks/user';
import { useCurrentUserVerifierStaff } from 'src/hooks/verifierStaff';
import { getIsPathMatchLocation } from 'src/utils/router';

import { RedirectPage } from '../common';

export const RequiredActionsGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation();

  const { user } = useCurrentUser();
  const { verifierStaff, loading: verifierStaffLoading } = useCurrentUserVerifierStaff();

  const { verifier, stripeCustomerId } = React.useMemo(() => {
    return {
      verifier: verifierStaff?.verifier,
      stripeCustomerId: verifierStaff?.stripeCustomerId ?? verifierStaff?.verifier.stripeCustomerId,
    };
  }, [verifierStaff]);

  const { paymentMethods, loadingPaymentMethods } =
    useStripeCustomerPaymentMethods(stripeCustomerId);

  const {
    isMfaRequired,
    isMfaEnabled,
    loading: isMfaStatusLoading,
  } = useCurrentUserAuthStatus({
    queryParams: { skip: !user },
  });

  const { isMaintenanceEnabled, loading: isMaintenanceStatusLoading } = useMaintenanceStatus(
    location.pathname,
  );

  const isAuthMfaSetupRoute = React.useMemo(() => {
    return getIsPathMatchLocation(location, appRoutes.auth.setupMfa);
  }, [location]);

  const isAuthPaymentMethodsSetupRoute = React.useMemo(() => {
    return getIsPathMatchLocation(location, appRoutes.auth.setupPaymentMethods);
  }, [location]);

  const isMaintenanceSetupRoute = React.useMemo(() => {
    return getIsPathMatchLocation(location, appRoutes.maintenance.index);
  }, [location]);

  if (
    isMaintenanceStatusLoading ||
    isMfaStatusLoading ||
    verifierStaffLoading ||
    loadingPaymentMethods
  ) {
    return <FullscreenLoader />;
  }

  // Block the application for all users,
  // in case maintenance is in progress

  if (isMaintenanceEnabled && !isMaintenanceSetupRoute) {
    return <Navigate to={appRoutes.maintenance.index} />;
  }

  if (!isMaintenanceEnabled && isMaintenanceSetupRoute) {
    return <RedirectPage />;
  }

  // Block the application for managerial roles (admins and managers),
  // in case there is a requirement to install the MFA

  if (isMfaRequired && !isMfaEnabled && !isAuthMfaSetupRoute) {
    return <Navigate to={appRoutes.auth.setupMfa} />;
  }

  // We block the application for commercial verifiers,
  // because they need to set up payment methods

  if (
    verifier &&
    verifier.type === 'Commercial' &&
    paymentMethods.length === 0 &&
    !isAuthPaymentMethodsSetupRoute &&
    !isAuthMfaSetupRoute
  ) {
    return <Navigate to={appRoutes.auth.setupPaymentMethods} />;
  }

  return <React.Fragment>{children}</React.Fragment>;
};
